import { Button, Input, Select, Text } from '@viapeople/components'
import { useEffect, useMemo, useRef, useState } from 'react'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { trpc } from '../../../utils/trpc'

interface FeatureFlagHistoryEntry {
	id: string
	featureFlagId: string
	featureFlagKey?: string
	featureFlagName?: string
	organizationId: string | null
	organizationName?: string | null
	action: 'create' | 'update' | 'delete' | 'enable' | 'disable'
	performedBy: string
	performedByEmail?: string
	performedByFirstName?: string
	performedByLastName?: string
	performedAt: Date
	details: Record<string, unknown> | null
}

interface FeatureFlagHistoryTabProps {
	featureFlags: Array<{ id: string; key: string; name: string }>
	organizations: Array<{ id: string; name: string }>
	users: Array<{ id: string; email: string; firstName: string; lastName: string }>
}

const actionLabels: Record<string, string> = {
	create: 'Created',
	update: 'Updated',
	delete: 'Deleted',
	enable: 'Enabled',
	disable: 'Disabled',
}

const actionColors: Record<string, string> = {
	create: 'text-success-700 bg-success-50',
	update: 'text-info-700 bg-info-50',
	delete: 'text-error-700 bg-error-50',
	enable: 'text-primary-700 bg-primary-50',
	disable: 'text-neutral-700 bg-neutral-50',
}

export const FeatureFlagHistoryTab = ({ featureFlags, organizations, users }: FeatureFlagHistoryTabProps) => {
	const [filters, setFilters] = useState({
		featureFlagId: '',
		organizationId: '',
		performedBy: '',
		action: '' as const,
		startDate: '',
		endDate: '',
		page: 1,
		perPage: 20,
		sortBy: 'performedAt' as const,
		order: 'desc' as 'asc' | 'desc',
	})

	// Autocomplete states
	const [featureFlagSearch, setFeatureFlagSearch] = useState('')
	const [organizationSearch, setOrganizationSearch] = useState('')
	const [userSearch, setUserSearch] = useState('')
	const [isFeatureFlagOpen, setIsFeatureFlagOpen] = useState(false)
	const [isOrganizationOpen, setIsOrganizationOpen] = useState(false)
	const [isUserOpen, setIsUserOpen] = useState(false)

	// Refs for click outside detection
	const featureFlagRef = useRef<HTMLDivElement>(null)
	const organizationRef = useRef<HTMLDivElement>(null)
	const userRef = useRef<HTMLDivElement>(null)

	// Close dropdowns when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (featureFlagRef.current && !featureFlagRef.current.contains(event.target as Node)) {
				setIsFeatureFlagOpen(false)
			}
			if (organizationRef.current && !organizationRef.current.contains(event.target as Node)) {
				setIsOrganizationOpen(false)
			}
			if (userRef.current && !userRef.current.contains(event.target as Node)) {
				setIsUserOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const formatDate = (date: Date) => {
		return new Date(date).toLocaleString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		})
	}

	const getUserDisplayName = (entry: FeatureFlagHistoryEntry) => {
		if (entry.performedByFirstName && entry.performedByLastName) {
			return `${entry.performedByFirstName} ${entry.performedByLastName}`
		}
		return entry.performedByEmail || entry.performedBy || 'Unknown'
	}

	const columns: Column<FeatureFlagHistoryEntry>[] = [
		{
			field: 'performedAt',
			label: 'Date & Time',
			colSpan: 2,
			sortable: true,
			render: (entry: FeatureFlagHistoryEntry) => (
				<Text variant='xsmall' className='text-neutral-700'>
					{formatDate(entry.performedAt)}
				</Text>
			),
		},
		{
			field: 'action',
			label: 'Action',
			colSpan: 1,
			sortable: true,
			render: (entry: FeatureFlagHistoryEntry) => (
				<span
					className={`px-2 py-1 rounded text-xs font-semibold ${actionColors[entry.action] || actionColors.disable}`}>
					{actionLabels[entry.action] || entry.action}
				</span>
			),
		},
		{
			field: 'featureFlagName',
			label: 'Feature Flag',
			colSpan: 2,
			sortable: true,
			render: (entry: FeatureFlagHistoryEntry) => (
				<div>
					<Text variant='xsmall' weight='semibold' className='text-neutral-900'>
						{entry.featureFlagName || 'Unknown'}
					</Text>
					{entry.featureFlagKey && (
						<Text variant='xsmall' className='text-neutral-500'>
							{entry.featureFlagKey}
						</Text>
					)}
				</div>
			),
		},
		{
			field: 'organizationName',
			label: 'Organization',
			colSpan: 2,
			sortable: true,
			render: (entry: FeatureFlagHistoryEntry) => (
				<Text variant='xsmall' className='text-neutral-700'>
					{entry.organizationName || '-'}
				</Text>
			),
		},
		{
			field: 'performedByEmail',
			label: 'User',
			colSpan: 2,
			sortable: true,
			render: (entry: FeatureFlagHistoryEntry) => (
				<Text variant='xsmall' className='text-neutral-700'>
					{getUserDisplayName(entry)}
				</Text>
			),
		},
	]

	const handleFilterChange = (key: string, value: string) => {
		setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
	}

	const clearFilters = () => {
		setFilters({
			featureFlagId: '',
			organizationId: '',
			performedBy: '',
			action: '',
			startDate: '',
			endDate: '',
			page: 1,
			perPage: 20,
			sortBy: 'performedAt',
			order: 'desc',
		})
		setFeatureFlagSearch('')
		setOrganizationSearch('')
		setUserSearch('')
		setIsFeatureFlagOpen(false)
		setIsOrganizationOpen(false)
		setIsUserOpen(false)
	}

	const hasActiveFilters = useMemo(() => {
		return !!(
			filters.featureFlagId ||
			filters.organizationId ||
			filters.performedBy ||
			filters.action ||
			filters.startDate ||
			filters.endDate
		)
	}, [filters])

	// Filtered options for autocomplete
	const filteredFeatureFlags = useMemo(() => {
		if (!featureFlagSearch.trim()) return featureFlags
		const searchLower = featureFlagSearch.toLowerCase()
		return featureFlags.filter(
			(flag) => flag.name.toLowerCase().includes(searchLower) || flag.key.toLowerCase().includes(searchLower),
		)
	}, [featureFlags, featureFlagSearch])

	const filteredOrganizations = useMemo(() => {
		if (!organizationSearch.trim()) return organizations
		const searchLower = organizationSearch.toLowerCase()
		return organizations.filter((org) => org.name.toLowerCase().includes(searchLower))
	}, [organizations, organizationSearch])

	const filteredUsers = useMemo(() => {
		if (!userSearch.trim()) return users
		const searchLower = userSearch.toLowerCase()
		return users.filter(
			(user) =>
				user.firstName.toLowerCase().includes(searchLower) ||
				user.lastName.toLowerCase().includes(searchLower) ||
				user.email.toLowerCase().includes(searchLower),
		)
	}, [users, userSearch])

	// Get selected item labels
	const selectedFeatureFlag = featureFlags.find((flag) => flag.id === filters.featureFlagId)
	const selectedOrganization = organizations.find((org) => org.id === filters.organizationId)
	const selectedUser = users.find((user) => user.id === filters.performedBy)

	return (
		<div className='flex flex-col h-full'>
			<div className='mb-4 space-y-4 flex-shrink-0'>
				<div className='grid grid-cols-3 gap-4'>
					{/* Feature Flag Autocomplete */}
					<div ref={featureFlagRef} className='relative'>
						<Text variant='small' weight='semibold' className='mb-2'>
							Feature Flag
						</Text>
						<div className='relative'>
							<Input
								type='text'
								value={
									isFeatureFlagOpen
										? featureFlagSearch
										: selectedFeatureFlag
											? `${selectedFeatureFlag.name} (${selectedFeatureFlag.key})`
											: ''
								}
								onChange={(e) => {
									setFeatureFlagSearch(e.target.value)
									setIsFeatureFlagOpen(true)
									if (!e.target.value) {
										handleFilterChange('featureFlagId', '')
									}
								}}
								onFocus={() => setIsFeatureFlagOpen(true)}
								placeholder='Search feature flags...'
								className='pr-10'
							/>
							<button
								type='button'
								onClick={() => {
									setIsFeatureFlagOpen(!isFeatureFlagOpen)
									if (!isFeatureFlagOpen) {
										setFeatureFlagSearch(
											selectedFeatureFlag ? `${selectedFeatureFlag.name} (${selectedFeatureFlag.key})` : '',
										)
									}
								}}
								className='absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600'>
								<svg
									className={`w-5 h-5 transition-transform ${isFeatureFlagOpen ? 'transform rotate-180' : ''}`}
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
									aria-hidden='true'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
								</svg>
							</button>
						</div>
						{isFeatureFlagOpen && (
							<div className='absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-sm shadow-lg max-h-60 overflow-auto'>
								<div className='p-2'>
									<button
										type='button'
										onClick={() => {
											handleFilterChange('featureFlagId', '')
											setFeatureFlagSearch('')
											setIsFeatureFlagOpen(false)
										}}
										className={`w-full text-left px-3 py-2 rounded hover:bg-neutral-100 ${
											!filters.featureFlagId ? 'bg-primary-50 text-primary-700' : ''
										}`}>
										All feature flags
									</button>
								</div>
								{filteredFeatureFlags.length > 0 ? (
									<div className='border-t border-neutral-200'>
										{filteredFeatureFlags.map((flag) => (
											<button
												key={flag.id}
												type='button'
												onClick={() => {
													handleFilterChange('featureFlagId', flag.id)
													setFeatureFlagSearch(`${flag.name} (${flag.key})`)
													setIsFeatureFlagOpen(false)
												}}
												className={`w-full text-left px-3 py-2 hover:bg-neutral-100 ${
													filters.featureFlagId === flag.id ? 'bg-primary-50 text-primary-700' : ''
												}`}>
												<div className='font-medium'>{flag.name}</div>
												<div className='text-xs text-neutral-500'>{flag.key}</div>
											</button>
										))}
									</div>
								) : (
									<div className='p-3 text-sm text-neutral-500 text-center'>No feature flags found</div>
								)}
							</div>
						)}
					</div>

					{/* Organization Autocomplete */}
					<div ref={organizationRef} className='relative'>
						<Text variant='small' weight='semibold' className='mb-2'>
							Organization
						</Text>
						<div className='relative'>
							<Input
								type='text'
								value={isOrganizationOpen ? organizationSearch : selectedOrganization ? selectedOrganization.name : ''}
								onChange={(e) => {
									setOrganizationSearch(e.target.value)
									setIsOrganizationOpen(true)
									if (!e.target.value) {
										handleFilterChange('organizationId', '')
									}
								}}
								onFocus={() => setIsOrganizationOpen(true)}
								placeholder='Search organizations...'
								className='pr-10'
							/>
							<button
								type='button'
								onClick={() => {
									setIsOrganizationOpen(!isOrganizationOpen)
									if (!isOrganizationOpen) {
										setOrganizationSearch(selectedOrganization ? selectedOrganization.name : '')
									}
								}}
								className='absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600'>
								<svg
									className={`w-5 h-5 transition-transform ${isOrganizationOpen ? 'transform rotate-180' : ''}`}
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
									aria-hidden='true'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
								</svg>
							</button>
						</div>
						{isOrganizationOpen && (
							<div className='absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-sm shadow-lg max-h-60 overflow-auto'>
								<div className='p-2'>
									<button
										type='button'
										onClick={() => {
											handleFilterChange('organizationId', '')
											setOrganizationSearch('')
											setIsOrganizationOpen(false)
										}}
										className={`w-full text-left px-3 py-2 rounded hover:bg-neutral-100 ${
											!filters.organizationId ? 'bg-primary-50 text-primary-700' : ''
										}`}>
										All organizations
									</button>
								</div>
								{filteredOrganizations.length > 0 ? (
									<div className='border-t border-neutral-200'>
										{filteredOrganizations.map((org) => (
											<button
												key={org.id}
												type='button'
												onClick={() => {
													handleFilterChange('organizationId', org.id)
													setOrganizationSearch(org.name)
													setIsOrganizationOpen(false)
												}}
												className={`w-full text-left px-3 py-2 hover:bg-neutral-100 ${
													filters.organizationId === org.id ? 'bg-primary-50 text-primary-700' : ''
												}`}>
												{org.name}
											</button>
										))}
									</div>
								) : (
									<div className='p-3 text-sm text-neutral-500 text-center'>No organizations found</div>
								)}
							</div>
						)}
					</div>

					{/* User Autocomplete */}
					<div ref={userRef} className='relative'>
						<Text variant='small' weight='semibold' className='mb-2'>
							User
						</Text>
						<div className='relative'>
							<Input
								type='text'
								value={
									isUserOpen
										? userSearch
										: selectedUser
											? `${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.email})`
											: ''
								}
								onChange={(e) => {
									setUserSearch(e.target.value)
									setIsUserOpen(true)
									if (!e.target.value) {
										handleFilterChange('performedBy', '')
									}
								}}
								onFocus={() => setIsUserOpen(true)}
								placeholder='Search users...'
								className='pr-10'
							/>
							<button
								type='button'
								onClick={() => {
									setIsUserOpen(!isUserOpen)
									if (!isUserOpen) {
										setUserSearch(
											selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.email})` : '',
										)
									}
								}}
								className='absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600'>
								<svg
									className={`w-5 h-5 transition-transform ${isUserOpen ? 'transform rotate-180' : ''}`}
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
									aria-hidden='true'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
								</svg>
							</button>
						</div>
						{isUserOpen && (
							<div className='absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-sm shadow-lg max-h-60 overflow-auto'>
								<div className='p-2'>
									<button
										type='button'
										onClick={() => {
											handleFilterChange('performedBy', '')
											setUserSearch('')
											setIsUserOpen(false)
										}}
										className={`w-full text-left px-3 py-2 rounded hover:bg-neutral-100 ${
											!filters.performedBy ? 'bg-primary-50 text-primary-700' : ''
										}`}>
										All users
									</button>
								</div>
								{filteredUsers.length > 0 ? (
									<div className='border-t border-neutral-200'>
										{filteredUsers.map((user) => (
											<button
												key={user.id}
												type='button'
												onClick={() => {
													handleFilterChange('performedBy', user.id)
													setUserSearch(`${user.firstName} ${user.lastName} (${user.email})`)
													setIsUserOpen(false)
												}}
												className={`w-full text-left px-3 py-2 hover:bg-neutral-100 ${
													filters.performedBy === user.id ? 'bg-primary-50 text-primary-700' : ''
												}`}>
												<div className='font-medium'>
													{user.firstName} {user.lastName}
												</div>
												<div className='text-xs text-neutral-500'>{user.email}</div>
											</button>
										))}
									</div>
								) : (
									<div className='p-3 text-sm text-neutral-500 text-center'>No users found</div>
								)}
							</div>
						)}
					</div>
				</div>

				<div className='grid grid-cols-4 gap-4'>
					<div>
						<Text variant='small' weight='semibold' className='mb-2'>
							Action Type
						</Text>
						<Select
							value={filters.action}
							onChange={(value) => handleFilterChange('action', value as string)}
							placeholder='All actions'
							options={[
								{ value: '', label: 'All actions' },
								{ value: 'create', label: 'Created' },
								{ value: 'update', label: 'Updated' },
								{ value: 'delete', label: 'Deleted' },
								{ value: 'enable', label: 'Enabled' },
								{ value: 'disable', label: 'Disabled' },
							]}
						/>
					</div>

					<div>
						<Text variant='small' weight='semibold' className='mb-2'>
							Start Date
						</Text>
						<Input
							type='date'
							value={filters.startDate}
							onChange={(e) => handleFilterChange('startDate', e.target.value)}
						/>
					</div>

					<div>
						<Text variant='small' weight='semibold' className='mb-2'>
							End Date
						</Text>
						<Input
							type='date'
							value={filters.endDate}
							onChange={(e) => handleFilterChange('endDate', e.target.value)}
						/>
					</div>

					<div className='flex items-end'>
						{hasActiveFilters && (
							<Button variant='secondary' onClick={clearFilters} size='small'>
								Clear Filters
							</Button>
						)}
					</div>
				</div>
			</div>

			<div className='flex-1 min-h-0 overflow-y-auto'>
				<GenericTable<
					FeatureFlagHistoryEntry,
					{ page: number; perPage: number; sortBy?: string; order?: 'asc' | 'desc' }
				>
					columns={columns}
					idField='id'
					showSearch={false}
					showPagination={true}
					onRowClick={undefined}
					initialSort={{ sortBy: 'performedAt', order: 'desc' }}
					queryOptionsGenerator={(options: {
						page: number
						perPage: number
						sortBy?: string
						order?: 'asc' | 'desc'
					}) =>
						trpc.admin.featureFlagsHistoryPage.getHistory.queryOptions({
							...filters,
							featureFlagId: filters.featureFlagId || undefined,
							organizationId: filters.organizationId || undefined,
							performedBy: filters.performedBy || undefined,
							action: filters.action || undefined,
							startDate: filters.startDate || undefined,
							endDate: filters.endDate || undefined,
							page: options.page,
							perPage: options.perPage,
							sortBy: (options.sortBy || 'performedAt') as
								| 'performedAt'
								| 'action'
								| 'featureFlagName'
								| 'organizationName'
								| 'performedByEmail',
							order: options.order || 'desc',
						})
					}
					emptyState={{
						title: 'No history found',
						description: hasActiveFilters
							? 'Try adjusting your filters'
							: 'Feature flag history will appear here as actions are performed',
					}}
				/>
			</div>
		</div>
	)
}
